import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { getAppointmentStats, getUpcomingAppointments } from '../services/appointmentsService';
import socketClient from '../services/socketClient';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import { keyframes } from '@mui/system';
import Fade from '@mui/material/Fade';
import StandardCard from './common/StandardCard';
import DashboardHero from './shared/DashboardHero';
import DashboardStatCard from './shared/DashboardStatCard';
import ExpandableCard from './shared/ExpandableCard';
import ViewModeBanner from './shared/ViewModeBanner';
import ClinicSelector from './shared/ClinicSelector';
import StartVideoCallModal from './StartVideoCallModal';
import CalendarView from './shared/CalendarView';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';

// Icons
import VideocamIcon from '@mui/icons-material/Videocam';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MessageIcon from '@mui/icons-material/Message';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import NotificationBell from './notifications/NotificationBell';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import CopyrightIcon from '@mui/icons-material/Copyright';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
// New icons for patient connected services
import ScienceIcon from '@mui/icons-material/Science';
import RadiologyIcon from '@mui/icons-material/MedicalServices';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import StoreIcon from '@mui/icons-material/Store';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import HistoryIcon from '@mui/icons-material/History';
import ShareIcon from '@mui/icons-material/Share';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Removed unused pulse animation

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const Dashboard = () => {
  // Auth context
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  
  // Note: Removed auto-redirect logic to allow super admins to view patient dashboard
  // Patients can use this dashboard, and admins can view it via "View As" menu
  
  // UI state
  const [anchorEl, setAnchorEl] = useState(null);
  const [_isLoading, setIsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  
  // Data state - real data, empty until features are built
  const [stats, setStats] = useState({ total: 0, upcoming: 0, past: 0 });
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [recentMessages] = useState([]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  
  // Dynamic health stats - showing only 2 most important stats
  const healthStats = [
    {
      id: 1,
      title: 'Total Appointments',
      value: stats.total || 0,
      status: `${stats.past || 0} completed`,
      icon: <CalendarMonthIcon />,
      color: 'primary'
    },
    {
      id: 2,
      title: 'Upcoming Appointments',
      value: stats.upcoming || 0,
      status: stats.upcoming > 0 ? 'Scheduled' : 'No appointments',
      icon: <VideocamIcon />,
      color: stats.upcoming > 0 ? 'success' : 'info'
    }
  ];

  // Fetch real data from Supabase on component mount
  useEffect(() => {
    async function loadDashboardData() {
      if (!user) return;
      
      try {
        setIsLoading(true);
        
        // Fetch appointment statistics and upcoming appointments
        const [statsResult, appointmentsResult] = await Promise.all([
          getAppointmentStats(),
          getUpcomingAppointments(user.id, 5)
        ]);
        
        // Handle API responses
        if (statsResult.data) {
          setStats(statsResult.data);
        }
        
        if (appointmentsResult.data) {
          setUpcomingAppointments(appointmentsResult.data);
        }
        
      } catch (error) {
        // Suppress connection errors (expected when backend is down)
        // Only log non-connection errors in development
        const errorMessage = error?.message || error?.toString() || '';
        const isConnectionError = error?.isConnectionError ||
                                  errorMessage.includes('Backend server is not available') ||
                                  errorMessage.includes('Failed to fetch') || 
                                  errorMessage.includes('ERR_CONNECTION_REFUSED') ||
                                  (error instanceof TypeError && errorMessage.includes('fetch'));
        
        // Silently handle connection errors - don't log or show errors
        if (!isConnectionError && import.meta.env.DEV) {
          console.warn('Error loading dashboard data:', errorMessage);
        }
      } finally {
        setIsLoading(false);
      }
    }
    
    loadDashboardData();

    // Connect to socket for real-time notifications
    socketClient.connect();

    // Listen for notifications
    const handleNotification = (notification) => {
      // Show notification toast
      toast.success(notification.message || 'New notification');
      
      // Refresh dashboard data if needed
      if (notification.type === 'appointment' || notification.type === 'message') {
        loadDashboardData();
      }
    };

    socketClient.on('notification:new', handleNotification);

    // Listen for appointment updates
    const handleAppointmentUpdate = () => {
      loadDashboardData();
    };

    socketClient.on('appointment:updated', handleAppointmentUpdate);

    // Cleanup
    return () => {
      socketClient.off('notification:new', handleNotification);
      socketClient.off('appointment:updated', handleAppointmentUpdate);
    };
  }, [user]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      // Navigate to login page immediately after signout
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
      // Even if signout fails, navigate to login
      navigate('/login', { replace: true });
    }
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };


  return (
    <Box sx={{ 
      flexGrow: 1, 
      bgcolor: 'background.default', 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <ViewModeBanner />
      {/* Header */}
      <AppBar 
        role="navigation"
        aria-label="Main Navigation Bar"
        position="static" 
        color="primary" 
        elevation={2}
        sx={{
          borderBottom: 'none',
          bgcolor: 'primary.dark',
          color: 'common.white',
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 }, width: '100%', maxWidth: '100%' }}>
          <Toolbar disableGutters>
            <Box 
              component="div" 
              sx={{ 
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                animation: `${slideIn} 0.5s ease-out`,
              }}
            >
              <ClinicSelector />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotificationBell />
              <Tooltip title="Account">
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenu}
                  color="inherit"
                  sx={{ 
                    transition: 'transform 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    }
                  }}
                >
                  <AccountCircleIcon />
                </IconButton>
              </Tooltip>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    minWidth: 180,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    borderRadius: 2,
                  }
                }}
              >
                <MenuItem component={Link} to="/profile" onClick={handleClose}>
                  <PersonIcon fontSize="small" sx={{ mr: 1 }} />
                  Profile
                </MenuItem>
                <MenuItem component={Link} to="/settings" onClick={handleClose}>
                  <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
                  Settings
                </MenuItem>
                <MenuItem component={Link} to="/contact" onClick={handleClose}>
                  <MessageIcon fontSize="small" sx={{ mr: 1 }} />
                  Contact Us
                </MenuItem>
                <MenuItem component={Link} to="/admin" onClick={handleClose}>
                  <DashboardIcon fontSize="small" sx={{ mr: 1 }} />
                  Admin Dashboard
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                  <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      
      {/* Hero Section */}
      <DashboardHero
        greeting={`Welcome Back${profile?.first_name ? `, ${profile.first_name}` : ''}!`}
        description="Access your appointments, messages, and health stats."
        showVideoCallButton={true}
        showScheduleButton={true}
        scheduleButtonDisabled={false}
        userType="patient"
        onStartVideoCall={handleOpenModal}
      />
      
      {/* Main Content */}
      <Container 
        maxWidth="lg" 
        sx={{ 
          mb: 6, 
          flex: 1, 
          px: { xs: 2, sm: 3 },
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <Box sx={{ maxWidth: 1100, mx: 'auto', width: '100%', overflow: 'hidden' }}>
          {/* Health Stats - Moved up for better visibility */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              mb: 4,
              borderRadius: 4,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              animation: `${fadeIn} 0.5s ease-out 0.1s both`,
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden',
            }}
          >
            <Typography variant="h6" gutterBottom sx={{
              fontWeight: 700,
              mb: { xs: 3, sm: 3.5, md: 4 },
              textAlign: 'center',
              fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' },
              color: 'text.primary',
              letterSpacing: '-0.01em',
            }}>
              Health Stats
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                flexWrap: 'wrap',
                justifyContent: { xs: 'stretch', sm: 'center' },
                alignItems: 'stretch',
                gap: { xs: 2, sm: 2.5, md: 3 },
                width: '100%',
                maxWidth: { xs: '100%', md: '700px' },
                mx: 'auto',
              }}
            >
              <Box sx={{ 
                flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 10px)' }, 
                minWidth: { xs: '100%', sm: '280px' },
                maxWidth: { xs: '100%', sm: 'calc(50% - 10px)' }
              }}>
                <DashboardStatCard
                  label={healthStats[0].title}
                  value={healthStats[0].value}
                  sublabel={healthStats[0].status}
                  icon={healthStats[0].icon}
                  color={healthStats[0].color}
                />
              </Box>
              <Box sx={{ 
                flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 10px)' }, 
                minWidth: { xs: '100%', sm: '280px' },
                maxWidth: { xs: '100%', sm: 'calc(50% - 10px)' }
              }}>
                <DashboardStatCard
                  label={healthStats[1].title}
                  value={healthStats[1].value}
                  sublabel={healthStats[1].status}
                  icon={healthStats[1].icon}
                  color={healthStats[1].color}
                />
              </Box>
            </Box>
          </Paper>

          {/* Find Physicians Section */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              mb: 4,
              borderRadius: 4,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              animation: `${fadeIn} 0.5s ease-out 0.1s both`,
              width: '100%',
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Find a Physician
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Browse available physicians and request an appointment
              </Typography>
              <Button
                component={Link}
                to="/physicians"
                variant="contained"
                color="primary"
                size="large"
                startIcon={<PersonIcon />}
                sx={{
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Browse Physicians
              </Button>
            </Box>
          </Paper>

          {/* Appointments and Messages */}
          <Box sx={{ mb: 4 }}>
            {/* Upcoming Appointments */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                mb: 4,
                borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                animation: `${fadeIn} 0.5s ease-out 0.2s both`,
                width: '100%',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 3, md: 4 } }}>
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  fontSize: { xs: '1.1rem', md: '1.25rem' }
                }}>
                  Upcoming Appointments
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setCalendarOpen(true)}
                  startIcon={<CalendarMonthIcon />}
                >
                  View Calendar
                </Button>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr' },
                }}
              >
                {upcomingAppointments.map((appointment) => (
                  <Paper
                    key={appointment.id}
                    elevation={0}
                    sx={{
                      p: { xs: 2, md: 3 },
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.3s ease',
                      height: '100%',
                      width: '100%',
                      '&:hover': {
                        transform: 'translateX(4px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', width: '100%' }}>
                      <Avatar sx={{ bgcolor: 'primary.light', mr: 2, flexShrink: 0 }}>
                        <VideocamIcon />
                      </Avatar>
                      <Box sx={{ width: '100%', flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {appointment.doctor}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {appointment.specialty}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {appointment.date} at {appointment.time}
                        </Typography>
                        <Typography variant="body2" color="primary.main" sx={{ mt: 1 }}>
                          {appointment.type}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Paper>

            {/* Calendar Modal */}
            <Dialog
              open={calendarOpen}
              onClose={() => setCalendarOpen(false)}
              maxWidth="lg"
              fullWidth
              fullScreen={window.innerWidth < 600}
              PaperProps={{
                sx: {
                  height: { xs: '100vh', sm: '90vh' },
                  maxHeight: { xs: '100vh', sm: '90vh' },
                  m: { xs: 0, sm: 2 }
                }
              }}
            >
              <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">Appointment Calendar</Typography>
                  <IconButton onClick={() => setCalendarOpen(false)} size="small">
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent sx={{ p: 0, pt: 1, overflow: 'hidden' }}>
                <CalendarView 
                  height={window.innerHeight * 0.75}
                  showToolbar={true}
                  onSelectSlot={({ start }) => {
                    setCalendarOpen(false);
                    navigate(`/appointments?date=${start.toISOString()}`);
                  }}
                  // onSelectEvent removed - CalendarView now shows popover with management options
                />
              </DialogContent>
            </Dialog>
            
            {/* Recent Messages */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                animation: `${fadeIn} 0.5s ease-out 0.3s both`,
                width: '100%',
              }}
            >
              <Typography variant="h6" gutterBottom sx={{
                fontWeight: 600,
                mb: { xs: 3, md: 4 },
                textAlign: 'center',
                fontSize: { xs: '1.1rem', md: '1.25rem' }
              }}>
                Recent Messages
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr' },
                }}
              >
                {recentMessages.map((message) => (
                  <Paper
                    key={message.id}
                    elevation={0}
                    sx={{
                      p: { xs: 2, md: 3 },
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.3s ease',
                      height: '100%',
                      width: '100%',
                      '&:hover': {
                        transform: 'translateX(4px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', width: '100%' }}>
                      <Avatar sx={{ bgcolor: 'secondary.light', mr: 2, flexShrink: 0 }}>
                        <MessageIcon />
                      </Avatar>
                      <Box sx={{ width: '100%', flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {message.from}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {message.date}
                        </Typography>
                        <Typography variant="body2">
                          {message.preview}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Paper>
          </Box>

          {/* Health Stats section moved up */}

          {/* My Health Records - Changed to list format */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              mb: 4,
              borderRadius: 4,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              width: '100%',
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
                mb: { xs: 2, sm: 3, md: 4 },
                textAlign: 'center',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
              }}
            >
              My Health Records
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {/* Medical History Item */}
              <ExpandableCard
                title="Medical History"
                description="View your complete medical history, including past diagnoses, treatments, and medications"
                icon={<HistoryIcon />}
                iconColor="primary"
                buttonText="Coming Soon"
                buttonColor="primary"
                disabled={true}
              />
              
              {/* Share Media Item */}
              <ExpandableCard
                title="Share Media"
                description="Upload photos & videos to share with doctors for better diagnosis and consultation"
                icon={<PhotoCameraIcon />}
                iconColor="secondary"
                buttonText="Coming Soon"
                buttonColor="secondary"
                disabled={true}
              />
              
              {/* Share Records Item */}
              <ExpandableCard
                title="Share Records"
                description="Share health records with family members or other healthcare providers securely"
                icon={<ShareIcon />}
                iconColor="info"
                buttonText="Coming Soon"
                buttonColor="info"
                disabled={true}
              />
            </Box>
          </Paper>

          {/* Connected Healthcare Services - Updated layout to match Health Stats */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              mb: 4,
              borderRadius: 4,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              width: '100%',
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
                mb: { xs: 2, sm: 3, md: 4 },
                textAlign: 'center',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
              }}
            >
              Connected Healthcare Services
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: { xs: 2, sm: 2.5, md: 3 },
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr' },
                gridTemplateRows: { xs: 'auto auto auto', sm: 'auto auto', md: 'auto auto' },
                gridTemplateAreas: {
                  xs: `
                    'pharmacy lab'
                    'imaging imaging'
                    'transport transport'
                  `,
                  sm: `
                    'pharmacy lab'
                    'imaging transport'
                  `,
                  md: `
                    'pharmacy lab'
                    'imaging transport'
                  `,
                },
                justifyItems: 'stretch',
                alignItems: 'stretch',
                mb: 2,
              }}
            >
              <Box sx={{ gridArea: 'pharmacy', height: '100%' }}>
                <StandardCard 
                  icon={LocalPharmacyIcon} 
                  title="Pharmacy" 
                  description="View prescriptions & pharmacy locations"
                  link="#" 
                  color="success"
                  buttonText="Coming Soon"
                  disabled={true}
                  showDescriptionOnHover={true}
                />
              </Box>
              <Box sx={{ gridArea: 'lab', height: '100%' }}>
                <StandardCard 
                  icon={ScienceIcon} 
                  title="Lab Results" 
                  description="Access your lab test results"
                  link="#" 
                  color="warning"
                  buttonText="Coming Soon"
                  disabled={true}
                  showDescriptionOnHover={true}
                />
              </Box>
              <Box sx={{ gridArea: 'imaging', height: '100%' }}>
                <StandardCard 
                  icon={RadiologyIcon} 
                  title="Imaging" 
                  description="View X-rays, MRIs & other scans"
                  link="#" 
                  color="info"
                  buttonText="Coming Soon"
                  disabled={true}
                  showDescriptionOnHover={true}
                />
              </Box>
              <Box sx={{ gridArea: 'transport', height: '100%' }}>
                <StandardCard 
                  icon={DirectionsCarIcon} 
                  title="Transportation" 
                  description="Book medical transport services"
                  link="#" 
                  color="primary"
                  buttonText="Coming Soon"
                  disabled={true}
                  showDescriptionOnHover={true}
                />
              </Box>
            </Box>
          </Paper>

          {/* Lifestyle & Support Services - Updated layout to match Health Stats */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              mb: 4,
              borderRadius: 4,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              width: '100%',
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
                mb: { xs: 2, sm: 3, md: 4 },
                textAlign: 'center',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
              }}
            >
              Lifestyle & Support Services
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: { xs: 2, sm: 2.5, md: 3 },
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr' },
                gridTemplateRows: { xs: 'auto auto', sm: 'auto', md: 'auto' },
                gridTemplateAreas: {
                  xs: `
                    'whatsapp stores'
                    'nutrition nutrition'
                  `,
                  sm: `
                    'whatsapp stores'
                    'nutrition nutrition'
                  `,
                  md: `
                    'whatsapp stores'
                    'nutrition nutrition'
                  `,
                },
                justifyItems: 'stretch',
                alignItems: 'stretch',
                mb: 2,
              }}
            >
              <Box sx={{ gridArea: 'whatsapp', height: '100%' }}>
                <StandardCard 
                  icon={WhatsAppIcon} 
                  title="WhatsApp Support" 
                  description="Connect with care team via WhatsApp"
                  link="#" 
                  color="success"
                  buttonText="Open WhatsApp"
                  disabled={false}
                  isWhatsApp={true}
                  whatsAppNumber="15551234567"
                  whatsAppMessage="Hello! I'm a patient from Alpha01 Medical Consultants and I need assistance with my healthcare."
                />
              </Box>
              <Box sx={{ gridArea: 'stores', height: '100%' }}>
                <StandardCard 
                  icon={StoreIcon} 
                  title="Health Stores" 
                  description="Find nearby health & wellness stores"
                  link="#" 
                  color="warning"
                  buttonText="Coming Soon"
                  disabled={true}
                  showDescriptionOnHover={true}
                />
              </Box>
              <Box sx={{ gridArea: 'nutrition', height: '100%' }}>
                <StandardCard 
                  icon={RestaurantIcon} 
                  title="Nutrition Services" 
                  description="Healthy meal delivery & nutrition guidance"
                  link="#" 
                  color="secondary"
                  buttonText="Coming Soon"
                  disabled={true}
                  showDescriptionOnHover={true}
                />
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>

      {/* Footer */}
      <Box 
        component="footer" 
        sx={{ 
          py: { xs: 5, md: 6 }, 
          bgcolor: 'primary.dark',
          color: 'white',
          mt: 'auto',
          borderTop: '1px solid',
          borderColor: 'primary.main',
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 }, width: '100%', maxWidth: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: { xs: 4, md: 0 },
              mb: 3,
            }}
          >
            <StartVideoCallModal
              open={modalOpen}
              handleClose={handleCloseModal}
              userType="patient"
            />
            {/* Logo, description, social */}
            <Box sx={{ flex: 1, minWidth: 220, mb: { xs: 3, md: 0 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  component={Link}
                  to="/"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    textDecoration: 'none',
                  }}
                >
                  <Box
                    component="img"
                    src="/images/logo.svg"
                    alt="Alpha01 Medical Consultants Logo - Telemedicine Platform"
                    aria-label="Alpha01 Medical Consultants Logo"
                    sx={{
                      height: { xs: 56, md: 40 },
                      mr: 1,
                      display: 'block',
                      borderRadius: 2,
                      padding: 0.5,
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      transition: 'transform 0.2s ease',
                      '&:hover': { transform: 'scale(1.05)' },
                    }}
                  />
                </Box>
              </Box>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 2, 
                  color: 'rgba(255,255,255,0.85)', 
                  fontWeight: 400, 
                  fontSize: { xs: '1.08rem', md: '1.15rem' }, 
                  fontStyle: 'italic',
                  letterSpacing: 0.1,
                  lineHeight: 1.7,
                }}
              >
                Providing intelligent healthcare delivery through technology.<br />
                Access quality healthcare from anywhere, anytime.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <IconButton color="inherit" size="small" sx={{ '&:hover': { color: 'secondary.light' } }}>
                  <FacebookIcon />
                </IconButton>
                <IconButton color="inherit" size="small" sx={{ '&:hover': { color: 'secondary.light' } }}>
                  <TwitterIcon />
                </IconButton>
                <IconButton color="inherit" size="small" sx={{ '&:hover': { color: 'secondary.light' } }}>
                  <InstagramIcon />
                </IconButton>
                <IconButton color="inherit" size="small" sx={{ '&:hover': { color: 'secondary.light' } }}>
                  <LinkedInIcon />
                </IconButton>
              </Box>
            </Box>
            {/* Quick Links */}
            <Box sx={{ flex: 1, minWidth: 180, mb: { xs: 3, md: 0 } }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Quick Links
              </Typography>
              <Box component="ul" sx={{ p: 0, m: 0, listStyle: 'none' }}>
                <Box component="li" sx={{ mb: 1 }}>
                  <Link to="/profile" style={{ color: 'white', textDecoration: 'none', fontWeight: 400 }}>
                    My Profile
                  </Link>
                </Box>
                <Box component="li" sx={{ mb: 1 }}>
                  <Link to="/settings" style={{ color: 'white', textDecoration: 'none', fontWeight: 400 }}>
                    Settings
                  </Link>
                </Box>
                <Box component="li" sx={{ mb: 1 }}>
                  <Link to="/contact" style={{ color: 'white', textDecoration: 'none', fontWeight: 400 }}>
                    Contact Us
                  </Link>
                </Box>
                <Box component="li" sx={{ mb: 1 }}>
                  <Link to="/admin" style={{ color: 'white', textDecoration: 'none', fontWeight: 400 }}>
                    Admin Dashboard
                  </Link>
                </Box>
              </Box>
            </Box>
            {/* Contact Info */}
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Contact Information
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255,255,255,0.85)' }}>
                123 Medical Center Drive
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255,255,255,0.85)' }}>
                San Francisco, CA 94143
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255,255,255,0.85)' }}>
                Phone: (555) 123-4567
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255,255,255,0.85)' }}>
                Email: info@alpha01med.com
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', color: 'rgba(255,255,255,0.7)' }}>
              <CopyrightIcon fontSize="small" sx={{ mr: 0.5 }} /> 2025 Alpha01 Medical Consultants. All rights reserved.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: { xs: 2, md: 0 } }}>
              <Link to="/privacy" style={{ color: 'white', textDecoration: 'none', fontSize: '0.95rem', fontWeight: 400 }}>
                Privacy Policy
              </Link>
              <Link to="/terms" style={{ color: 'white', textDecoration: 'none', fontSize: '0.95rem', fontWeight: 400 }}>
                Terms of Service
              </Link>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Dashboard;
